import { Injectable, inject, signal, computed } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, tap } from 'rxjs';
import { EvaluationService } from './evaluation.service';
import { EvaluationConfig, Motive, Language } from './evaluation.model';
import { ToasterService } from '../../layout/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class EvaluationStore {
  // Injected services
  private readonly evaluationService = inject(EvaluationService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  // Local state for modifications before saving
  private readonly localConfigs = signal<Map<Language, EvaluationConfig>>(new Map());

  // Triggers for save operations
  private readonly saveAllTrigger = signal<{ timestamp: number } | null>(null);
  private readonly deleteMotiveTrigger = signal<{ motiveId: string; timestamp: number } | null>(
    null
  );
  private readonly deleteSubMotiveTrigger = signal<{
    motiveId: string;
    subMotiveId: string;
    timestamp: number;
  } | null>(null);

  // UI state
  readonly selectedLanguage = signal<Language>('fr');

  // Computed to check if there are unsaved changes
  readonly hasUnsavedChanges = computed(() => this.localConfigs().size > 0);

  // Resource to load evaluation config from the API
  readonly evaluationConfigResource = rxResource({
    params: () => ({ lang: this.selectedLanguage() }),
    stream: ({ params }) => this.evaluationService.getEvaluationConfig(params.lang),
  });

  // Resource to save all modified configs
  readonly saveAllResource = rxResource({
    params: () => this.saveAllTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      const configs = Array.from(this.localConfigs().values());
      if (configs.length === 0) return EMPTY;
      return this.evaluationService.updateEvaluationConfig(configs).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('GLOBAL.SAVE_SUCCESS'));
            this.localConfigs.set(new Map());
            this.evaluationConfigResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('GLOBAL.SAVE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Resource to delete a motive
  readonly deleteMotiveResource = rxResource({
    params: () => this.deleteMotiveTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.evaluationService.deleteMotive(this.selectedLanguage(), params.motiveId).pipe(
        tap(() => {
          queueMicrotask(() => {
            const newMap = new Map(this.localConfigs());
            newMap.delete(this.selectedLanguage());
            this.localConfigs.set(newMap);
            this.evaluationConfigResource.reload();
            this.toasterService.success(this.translate.instant('EVALUATION.DELETE_MOTIVE_SUCCESS'));
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('EVALUATION.DELETE_MOTIVE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Resource to delete a sub-motive
  readonly deleteSubMotiveResource = rxResource({
    params: () => this.deleteSubMotiveTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      const current = this.currentConfig();
      if (!current) return EMPTY;

      const allMotives = [...current.negativeMotives, ...current.positiveMotives];
      const parentMotive = allMotives.find((m) => m.id === params.motiveId);
      if (!parentMotive) return EMPTY;

      const isNegative = current.negativeMotives.some((m) => m.id === params.motiveId);
      const key = isNegative ? 'negativeMotives' : 'positiveMotives';

      const updatedConfig = {
        ...current,
        [key]: current[key].map((m: Motive) =>
          m.id === params.motiveId
            ? { ...m, subMotives: m.subMotives?.filter((sm) => sm.id !== params.subMotiveId) }
            : m
        ),
      };

      return this.evaluationService.updateSingleConfig(updatedConfig).pipe(
        tap(() => {
          queueMicrotask(() => {
            const newMap = new Map(this.localConfigs());
            newMap.delete(this.selectedLanguage());
            this.localConfigs.set(newMap);
            this.evaluationConfigResource.reload();
            this.toasterService.success(
              this.translate.instant('EVALUATION.DELETE_SUBMOTIVE_SUCCESS')
            );
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('EVALUATION.DELETE_SUBMOTIVE_ERROR'));
          });
          throw error;
        })
      );
    },
  });

  // Publicly exposed signals
  readonly availableLanguages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'es', label: 'Español' },
    { code: 'it', label: 'Italiano' },
    { code: 'nl', label: 'Nederlands' },
    { code: 'pt', label: 'Português' },
  ];

  readonly currentConfig = computed(() => {
    const local = this.localConfigs().get(this.selectedLanguage());
    return local ?? this.evaluationConfigResource.value() ?? null;
  });

  readonly isLoading = computed(
    () =>
      this.evaluationConfigResource.isLoading() ||
      this.saveAllResource.isLoading() ||
      this.deleteMotiveResource.isLoading() ||
      this.deleteSubMotiveResource.isLoading()
  );

  readonly error = computed(() => this.evaluationConfigResource.error());

  // Mutations
  setLanguage(lang: string): void {
    this.selectedLanguage.set(lang as Language);
  }

  updateThreshold(threshold: number): void {
    const current = this.currentConfig();
    if (current) {
      this.updateLocalConfig({
        ...current,
        positiveRatingStartingFrom: threshold,
      } as EvaluationConfig);
    }
  }

  updateMotive(type: 'negative' | 'positive', motiveId: string, updates: Partial<Motive>): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      const updated = current[key].map((m: Motive) =>
        m.id === motiveId ? { ...m, ...updates } : m
      );
      this.updateLocalConfig({ ...current, [key]: updated } as EvaluationConfig);
    }
  }

  addMotive(type: 'negative' | 'positive', motive: Motive): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      this.updateLocalConfig({
        ...current,
        [key]: [...current[key], motive],
      } as EvaluationConfig);
    }
  }

  deleteMotive(type: 'negative' | 'positive', motiveId: string): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      this.updateLocalConfig({
        ...current,
        [key]: current[key].filter((m: Motive) => m.id !== motiveId),
      } as EvaluationConfig);
    }
  }

  deleteMotiveDirectly(motiveId: string): void {
    this.deleteMotiveTrigger.set({ motiveId, timestamp: Date.now() });
  }

  addSubMotive(type: 'negative' | 'positive', motiveId: string, subMotive: Motive): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      const updated = current[key].map((m: Motive) =>
        m.id === motiveId ? { ...m, subMotives: [...(m.subMotives || []), subMotive] } : m
      );
      this.updateLocalConfig({ ...current, [key]: updated } as EvaluationConfig);
    }
  }

  updateSubMotive(
    type: 'negative' | 'positive',
    motiveId: string,
    subMotiveId: string,
    updates: Partial<Motive>
  ): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      const updated = current[key].map((m: Motive) =>
        m.id === motiveId
          ? {
              ...m,
              subMotives: m.subMotives?.map((sm) =>
                sm.id === subMotiveId ? { ...sm, ...updates } : sm
              ),
            }
          : m
      );
      this.updateLocalConfig({ ...current, [key]: updated } as EvaluationConfig);
    }
  }

  deleteSubMotive(type: 'negative' | 'positive', motiveId: string, subMotiveId: string): void {
    const current = this.currentConfig();
    if (current) {
      const key = type === 'negative' ? 'negativeMotives' : 'positiveMotives';
      const updated = current[key].map((m: Motive) =>
        m.id === motiveId
          ? { ...m, subMotives: m.subMotives?.filter((sm) => sm.id !== subMotiveId) }
          : m
      );
      this.updateLocalConfig({ ...current, [key]: updated } as EvaluationConfig);
    }
  }

  deleteSubMotiveDirectly(motiveId: string, subMotiveId: string): void {
    this.deleteSubMotiveTrigger.set({ motiveId, subMotiveId, timestamp: Date.now() });
  }

  saveConfig(): void {
    // Ne sauvegarder que s'il y a des modifications
    if (!this.hasUnsavedChanges()) {
      queueMicrotask(() => {
        this.toasterService.info(this.translate.instant('GLOBAL.NO_CHANGES'));
      });
      return;
    }

    // Nettoyer les motifs et sous-motifs vides avant la sauvegarde
    this.cleanEmptyMotives();

    this.saveAllTrigger.set({ timestamp: Date.now() });
  }

  private cleanEmptyMotives(): void {
    const configs = Array.from(this.localConfigs().entries());
    const newMap = new Map<Language, EvaluationConfig>();

    configs.forEach(([lang, config]) => {
      const cleanedConfig = {
        ...config,
        negativeMotives: this.filterNonEmptyMotives(config.negativeMotives),
        positiveMotives: this.filterNonEmptyMotives(config.positiveMotives),
      };
      newMap.set(lang, cleanedConfig);
    });

    this.localConfigs.set(newMap);
  }

  private filterNonEmptyMotives(motives: Motive[]): Motive[] {
    return motives
      .filter((motive) => this.isMotiveValid(motive))
      .map((motive) => ({
        ...motive,
        subMotives: motive.subMotives
          ? motive.subMotives.filter((subMotive) => this.isMotiveValid(subMotive))
          : undefined,
      }));
  }

  private isMotiveValid(motive: Motive): boolean {
    // Un motif est valide s'il a un titre ET une clé SF non vides
    return motive.title?.trim().length > 0 && motive.title_key_SF?.trim().length > 0;
  }

  private updateLocalConfig(config: EvaluationConfig): void {
    const newMap = new Map(this.localConfigs());
    newMap.set(this.selectedLanguage(), config);
    this.localConfigs.set(newMap);
  }
}
